// frontend/src/components/government/NSMakeUpPayTab.js
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Clock,
  CheckCircle,
  DollarSign,
  AlertTriangle,
  Calendar,
  Send,
  Info,
} from 'lucide-react';
import toast from '../../utils/toast';
import { staffAPI } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import { StatusBadge } from '../UI/StatusBadge';
import { CountBadge } from '../UI/CountBadge';
import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import { formatDate } from '../../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';

const formatSGD = (value) =>
  new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(value || 0);

const ACTIVITY_TYPES = [
  { value: 'ict', label: 'In-Camp Training (ICT)' },
  { value: 'reservist', label: 'Reservist Training' },
  { value: 'ippt', label: 'IPPT' },
  { value: 'ns_fit', label: 'NS FIT' },
  { value: 'mobilisation', label: 'Mobilisation' },
  { value: 'rt', label: 'Remedial Training (RT)' },
  { value: 'mr', label: 'Medical Review / Board' },
  { value: 'sv', label: 'SAF Volunteer Corps (SV) Activity' },
  { value: 'other', label: 'Other ORNS Activity' },
];

const PAYMENT_METHODS = [
  { value: 'direct', label: 'DIRECT — Employer reimbursed by MINDEF/MHA' },
  { value: 'non_direct', label: 'Non-DIRECT — NSman paid directly' },
];

// HR records a make-up pay claim and advances its MINDEF/MHA status.
const RECORD_STATUS_OPTIONS = [
  { value: 'verified', label: 'Verified (checked, not yet submitted)' },
  { value: 'submitted', label: 'Submitted to MINDEF/MHA' },
  { value: 'paid', label: 'Reimbursement received' },
  { value: 'rejected', label: 'Rejected' },
];

// Maps a tracker claim_status to a StatusBadge status + readable label
const STATUS_META = {
  to_claim: { badge: 'pending', label: 'To Claim' },
  pending: { badge: 'pending', label: 'Pending Verification' },
  verified: { badge: 'submitted', label: 'Verified' },
  submitted: { badge: 'submitted', label: 'Submitted to MINDEF' },
  paid: { badge: 'completed', label: 'Reimbursed' },
  rejected: { badge: 'rejected', label: 'Rejected' },
};

const STATUS_BUCKETS = [
  { value: 'all', label: 'All' },
  { value: 'to_claim', label: 'To Claim' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'reimbursed', label: 'Reimbursed' },
  { value: 'overdue', label: 'Overdue' },
];

const inBucket = (entry, bucket) => {
  switch (bucket) {
    case 'to_claim':
      return entry.claim_status === 'to_claim';
    case 'in_progress':
      return ['pending', 'verified', 'submitted'].includes(entry.claim_status);
    case 'reimbursed':
      return entry.claim_status === 'paid';
    case 'overdue':
      return entry.is_overdue;
    default:
      return true;
  }
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => ({
  value: String(y),
  label: String(y),
}));

const NSMakeUpPayTab = () => {
  const queryClient = useQueryClient();
  const [year, setYear] = useState(String(currentYear));
  const [statusBucket, setStatusBucket] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Record-from-leave modal state
  const [recordEntry, setRecordEntry] = useState(null);
  const [recordStatus, setRecordStatus] = useState('submitted');
  const [recordActivityType, setRecordActivityType] = useState('ict');
  const [recordPaymentMethod, setRecordPaymentMethod] = useState('direct');
  const [recordServicePay, setRecordServicePay] = useState('');
  const [recordActivityRef, setRecordActivityRef] = useState('');
  const [recordMindefRef, setRecordMindefRef] = useState('');
  const [recordReimbursed, setRecordReimbursed] = useState('');
  const [recordNotes, setRecordNotes] = useState('');

  // ---- Queries ----
  const { data, isLoading } = useQuery({
    queryKey: ['ns-makeup-tracker', { year }],
    queryFn: async () => {
      const res = await staffAPI.getNSMakeupTracker({ year });
      return res.data;
    },
    staleTime: 60 * 1000,
  });

  // ---- Mutations ----
  const recordMutation = useMutation({
    mutationFn: (payload) => staffAPI.recordNSMakeupFromLeave(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ns-makeup-tracker'] });
      queryClient.invalidateQueries({ queryKey: ['ns-makeup'] });
      toast.success('NS make-up pay claim updated');
      closeRecord();
    },
    onError: (error) => {
      const msg = error?.response?.data?.error || 'Failed to update claim';
      toast.error(String(msg));
    },
  });

  const summary = data?.summary || {};
  const entries = useMemo(() => data?.entries || [], [data]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return entries.filter((e) => {
      if (!inBucket(e, statusBucket)) return false;
      if (term) {
        const hay = `${e.employee_name || ''} ${e.staff_id || ''} ${e.leave_type_name || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [entries, statusBucket, searchTerm]);

  // ---- Record-from-leave handlers ----
  const openRecord = (entry, presetStatus) => {
    setRecordEntry(entry);
    setRecordStatus(presetStatus);
    setRecordActivityType(entry.activity_type || 'ict');
    setRecordPaymentMethod(entry.payment_method || 'direct');
    setRecordServicePay(entry.service_pay != null ? String(entry.service_pay) : '');
    setRecordActivityRef(entry.activity_reference || '');
    setRecordMindefRef(entry.mindef_reference_number || '');
    setRecordReimbursed(entry.reimbursed_amount != null ? String(entry.reimbursed_amount) : '');
    setRecordNotes('');
  };

  const closeRecord = () => {
    setRecordEntry(null);
    setRecordServicePay('');
    setRecordMindefRef('');
    setRecordReimbursed('');
    setRecordNotes('');
  };

  // Live make-up pay preview inside the record modal: income loss − service pay.
  const recordPreview = useMemo(() => {
    if (!recordEntry) return { incomeLoss: 0, makeupPay: 0 };
    const incomeLoss = recordEntry.income_loss || 0;
    const service = parseFloat(recordServicePay) || 0;
    return { incomeLoss, makeupPay: Math.max(0, incomeLoss - service) };
  }, [recordEntry, recordServicePay]);

  const submitRecord = (e) => {
    e.preventDefault();
    const payload = {
      leave_application_id: recordEntry.leave_application_id,
      claim_status: recordStatus,
      activity_type: recordActivityType,
      payment_method: recordPaymentMethod,
      service_pay: recordServicePay !== '' ? parseFloat(recordServicePay) : 0,
      ns_activity_reference: recordActivityRef || undefined,
      notes: recordNotes || undefined,
    };
    if (recordStatus === 'submitted') {
      payload.mindef_reference_number = recordMindefRef || undefined;
    }
    if (recordStatus === 'paid') {
      payload.mindef_reference_number = recordMindefRef || undefined;
      payload.reimbursed_amount = recordReimbursed !== '' ? parseFloat(recordReimbursed) : undefined;
    }
    recordMutation.mutate(payload);
  };

  const periodText = (entry) => {
    if (!entry.activity_start_date) return '—';
    const start = formatDate(entry.activity_start_date);
    const end = formatDate(entry.activity_end_date || entry.activity_start_date);
    return start === end ? start : `${start} – ${end}`;
  };

  const renderSubmissionDeadline = (entry) => {
    if (!entry.recommended_submission_date) return <span className="text-gray-400">—</span>;
    if (entry.claim_status === 'paid') {
      return <span className="text-gray-500">{formatDate(entry.recommended_submission_date)}</span>;
    }
    if (entry.is_overdue) {
      return (
        <span className="inline-flex items-center text-red-600 font-medium">
          <AlertTriangle className="w-3.5 h-3.5 mr-1" />
          Past lead time
        </span>
      );
    }
    const days = entry.days_until_recommended_submission;
    const soon = typeof days === 'number' && days <= 14;
    return (
      <span className={soon ? 'text-amber-600 font-medium' : 'text-gray-700'}>
        {formatDate(entry.recommended_submission_date)}
        {typeof days === 'number' && (
          <span className="block text-xs text-gray-400">{days} day(s) left</span>
        )}
      </span>
    );
  };

  const renderActions = (entry) => {
    const stop = (fn) => (e) => {
      e.stopPropagation();
      fn();
    };
    const isUnclaimed = ['to_claim', 'pending', 'verified'].includes(entry.claim_status);
    const isSubmitted = entry.claim_status === 'submitted';
    return (
      <div className="flex items-center justify-end gap-1">
        {isUnclaimed && (
          <button
            onClick={stop(() => openRecord(entry, 'submitted'))}
            className="icon-button-admin"
            title="Record make-up pay claim / mark submitted"
            aria-label={`Record NS make-up pay claim for ${entry.employee_name || entry.staff_id}`}
          >
            <Send className="w-4 h-4" />
          </button>
        )}
        {isSubmitted && (
          <button
            onClick={stop(() => openRecord(entry, 'paid'))}
            className="icon-button-admin"
            title="Mark reimbursement received"
            aria-label={`Mark NS make-up pay reimbursed for ${entry.employee_name || entry.staff_id}`}
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.to_claim_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    To Claim ({summary.to_claim_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Send className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.in_progress_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    In Progress ({summary.in_progress_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.reimbursed_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    Reimbursed ({summary.reimbursed_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-start">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.overdue_count || 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Past 2-wk lead time</p>
                </div>
              </div>
            </div>
          </div>

          {/* Toolbar: status buckets + filters */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
              {STATUS_BUCKETS.map((b) => (
                <button
                  key={b.value}
                  onClick={() => setStatusBucket(b.value)}
                  className={`tab-btn-admin tab-btn-admin-sm ${statusBucket === b.value ? 'active' : ''}`}
                >
                  {b.label}
                  {b.value === 'to_claim' && summary.to_claim_count > 0 && (
                    <CountBadge count={summary.to_claim_count} theme="admin" active={statusBucket === 'to_claim'} />
                  )}
                  {b.value === 'overdue' && summary.overdue_count > 0 && (
                    <CountBadge count={summary.overdue_count} theme="warning" active={statusBucket === 'overdue'} />
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <DebouncedSearchInput
                value={searchTerm}
                onSearch={setSearchTerm}
                placeholder="Search by employee or staff ID..."
                theme="admin"
                className="min-w-0"
              />
              <div className="w-28 flex-shrink-0">
                <FormSelect
                  options={YEAR_OPTIONS}
                  value={year}
                  onChange={(v) => setYear(typeof v === 'object' ? v?.target?.value : v)}
                />
              </div>
            </div>
          </div>

          {/* Tracker list */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border">
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No NS make-up pay to track</h3>
                <p className="text-sm text-gray-500 max-w-xl mx-auto">
                  No approved National Service leave for {year}. Employees appear here automatically
                  once they have an approved NS / ORNS leave.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">NSman</th>
                      <th className="table-header-cell">Activity</th>
                      <th className="table-header-cell">Activity Period</th>
                      <th className="table-header-cell">Est. Make-Up Pay</th>
                      <th className="table-header-cell">Scheme</th>
                      <th className="table-header-cell">Submit By</th>
                      <th className="table-header-cell">Status</th>
                      <th className="table-header-cell text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filtered.map((entry) => (
                      <tr
                        key={entry.leave_application_id}
                        onClick={() => openRecord(entry, entry.claim_status === 'submitted' ? 'paid' : 'submitted')}
                        className="table-row cursor-pointer"
                      >
                        <td className="table-cell">
                          <div className="font-medium text-gray-900">{entry.employee_name}</div>
                          <div className="text-xs text-gray-500">{entry.staff_id}</div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {entry.activity_type_display || 'NS Leave'}
                          </div>
                          {entry.activity_source === 'call_up' ? (
                            <div
                              className="text-[11px] text-sky-600"
                              title={`Activity pre-filled from the verified NS call-up${
                                entry.activity_callup_text ? ` (“${entry.activity_callup_text}”)` : ''
                              }. Confirm when recording.`}
                            >
                              from call-up
                            </div>
                          ) : (
                            <div className="text-[11px] text-gray-400">{entry.leave_type_name}</div>
                          )}
                        </td>
                        <td className="table-cell text-sm text-gray-700">
                          <div>{periodText(entry)}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatLeaveDays(entry.total_days)} {pluralizeDays(entry.total_days)}
                          </div>
                        </td>
                        <td className="table-cell font-semibold tabular-nums">
                          {formatSGD(entry.makeup_pay_amount)}
                          {entry.claim_status === 'to_claim' && (
                            <span
                              className="block text-[11px] text-amber-600"
                              title={`Gross civilian income loss${
                                entry.income_loss_basis ? ` (${entry.income_loss_basis})` : ''
                              }. Enter the NSman's Service Pay when recording to get the net make-up pay.`}
                            >
                              before service pay
                            </span>
                          )}
                          {entry.income_is_estimated && (
                            <span
                              className="block text-[11px] text-amber-600"
                              title="No active salary on file for this employee — income loss could not be estimated."
                            >
                              no salary on file
                            </span>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className="text-xs text-gray-600">
                            {entry.payment_method === 'non_direct' ? 'Non-DIRECT' : 'DIRECT'}
                          </span>
                        </td>
                        <td className="table-cell text-sm">{renderSubmissionDeadline(entry)}</td>
                        <td className="table-cell">
                          <StatusBadge
                            status={STATUS_META[entry.claim_status]?.badge || 'pending'}
                            label={STATUS_META[entry.claim_status]?.label}
                          />
                        </td>
                        <td className="table-cell">{renderActions(entry)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {filtered.map((entry) => (
                  <div
                    key={entry.leave_application_id}
                    onClick={() => openRecord(entry, entry.claim_status === 'submitted' ? 'paid' : 'submitted')}
                    className="bg-white rounded-2xl shadow-sm border p-4 hover:shadow-md transition-shadow cursor-pointer flex flex-col h-full"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">{entry.employee_name}</div>
                        <div className="text-xs text-gray-500">{entry.staff_id}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {entry.activity_type_display || 'NS Leave'}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <StatusBadge
                          status={STATUS_META[entry.claim_status]?.badge || 'pending'}
                          label={STATUS_META[entry.claim_status]?.label}
                        />
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 flex-1">{periodText(entry)}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500">
                        {formatLeaveDays(entry.total_days)} {pluralizeDays(entry.total_days)}
                      </span>
                      <span className="font-semibold tabular-nums">{formatSGD(entry.makeup_pay_amount)}</span>
                    </div>
                    <div className="mt-1 text-xs">Submit by: {renderSubmissionDeadline(entry)}</div>
                    <div className="card-action-footer justify-end gap-2">
                      {renderActions(entry)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Reference panel */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Info className="w-4 h-4 mr-2 text-blue-600" />
              How NS Make-Up Pay is tracked (ns.gov.sg)
            </h4>
            <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside">
              <li>Every approved <strong>National Service leave</strong> appears here automatically — one row per ORNS activity period.</li>
              <li><strong>Make-Up Pay = civilian income loss − Service Pay.</strong> We estimate the income loss from the employee’s salary (monthly ÷ 30 × activity days); the figure shown before recording is the <strong>gross</strong> loss. Enter the NSman’s Service Pay when recording for the net amount.</li>
              <li><strong>DIRECT scheme:</strong> keep paying the NSman his usual salary — MINDEF/MHA reimburses the employer directly.</li>
              <li>Submit at least <strong>2 weeks before</strong> the activity start date; payment is typically made within <strong>~10 working days</strong> from the activity start.</li>
              <li>MINDEF/MHA confirms the final amount from CPF wage data; the estimate here is for HR planning and verification.</li>
            </ul>
          </div>
        </>
      )}

      {/* Record-from-leave modal */}
      <Modal
        isOpen={!!recordEntry}
        onClose={closeRecord}
        title="Record NS Make-Up Pay Claim"
        size="md"
        theme="admin"
        footer={
          <>
            <button type="button" onClick={closeRecord} className="btn btn-admin-outline">
              Cancel
            </button>
            <button
              type="submit"
              form="ns-record-form"
              disabled={recordMutation.isPending}
              className="btn btn-success"
            >
              {recordMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {recordEntry && (
          <form id="ns-record-form" onSubmit={submitRecord} className="space-y-4">
            <div className="rounded-2xl bg-gray-50 p-4 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-red-600" />
                <span className="font-medium text-gray-900">{recordEntry.employee_name}</span>
                <span className="text-gray-500">({recordEntry.staff_id})</span>
              </div>
              <div className="text-gray-600">
                {recordEntry.leave_type_name} · {periodText(recordEntry)} ·{' '}
                {formatLeaveDays(recordEntry.total_days)} {pluralizeDays(recordEntry.total_days)}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Activity Type</label>
                <FormSelect
                  options={ACTIVITY_TYPES}
                  value={recordActivityType}
                  onChange={(v) => setRecordActivityType(typeof v === 'object' ? v?.target?.value : v)}
                />
                {recordEntry.activity_source === 'call_up' && recordEntry.activity_callup_text && (
                  <p className="text-xs text-sky-600 mt-1">
                    Pre-filled from the verified call-up (“{recordEntry.activity_callup_text}”). Adjust if needed.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Payment Scheme</label>
                <FormSelect
                  options={PAYMENT_METHODS}
                  value={recordPaymentMethod}
                  onChange={(v) => setRecordPaymentMethod(typeof v === 'object' ? v?.target?.value : v)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Call-up / Activity Reference</label>
              <input
                type="text"
                placeholder="e.g., NS-2026-001234"
                value={recordActivityRef}
                onChange={(e) => setRecordActivityRef(e.target.value)}
                className="input-admin w-full"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Service Pay credited (S$)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="From the NSman's NS Pay statement"
                value={recordServicePay}
                onChange={(e) => setRecordServicePay(e.target.value)}
                className="input-admin w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Service Pay is credited by MINDEF/MHA and is not in our system — enter it from the
                NSman’s NS Pay statement to compute the net make-up pay.
              </p>
            </div>

            {/* Live make-up pay preview */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs text-gray-500">Civilian Income Loss</div>
                  <div className="font-semibold text-gray-900">{formatSGD(recordPreview.incomeLoss)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Service Pay</div>
                  <div className="font-semibold text-gray-900">
                    {formatSGD(parseFloat(recordServicePay) || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Make-Up Pay</div>
                  <div className="font-bold text-emerald-700">{formatSGD(recordPreview.makeupPay)}</div>
                </div>
              </div>
              {recordEntry.income_loss_basis && (
                <p className="text-xs text-gray-600 mt-2 text-center">
                  Income loss = monthly civilian income × {recordEntry.income_loss_basis}.
                </p>
              )}
              {Array.isArray(recordEntry.income_loss_breakdown) &&
                recordEntry.income_loss_breakdown.length > 1 && (
                  <div className="mt-2 space-y-1">
                    {recordEntry.income_loss_breakdown.map((seg) => (
                      <div
                        key={seg.month}
                        className="flex items-center justify-between text-xs text-gray-500"
                      >
                        <span>
                          {seg.month_label}: {seg.activity_working_days} of {seg.month_working_days}{' '}
                          working days
                        </span>
                        <span className="text-gray-700">{formatSGD(seg.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              <p className="text-xs text-gray-400 mt-2 text-center">
                Employer cross-check only — the final amount is auto-generated by MINDEF/MHA from
                CPF wage data (Ordinary + qualifying Additional Wages).
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Claim Status</label>
              <FormSelect
                options={RECORD_STATUS_OPTIONS}
                value={recordStatus}
                onChange={(v) => setRecordStatus(typeof v === 'object' ? v?.target?.value : v)}
              />
            </div>

            {(recordStatus === 'submitted' || recordStatus === 'paid') && (
              <div className="form-group">
                <label className="form-label">MINDEF/MHA Reference Number</label>
                <input
                  type="text"
                  placeholder="Reference from MINDEF/MHA"
                  value={recordMindefRef}
                  onChange={(e) => setRecordMindefRef(e.target.value)}
                  className="input-admin w-full"
                />
              </div>
            )}

            {recordStatus === 'paid' && (
              <div className="form-group">
                <label className="form-label">Reimbursed Amount (S$)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Actual amount reimbursed by MINDEF/MHA"
                  value={recordReimbursed}
                  onChange={(e) => setRecordReimbursed(e.target.value)}
                  className="input-admin w-full"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                rows={2}
                value={recordNotes}
                onChange={(e) => setRecordNotes(e.target.value)}
                className="input-admin w-full"
                placeholder="Optional notes for this claim"
              />
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default NSMakeUpPayTab;
